import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  ImageIcon,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Maximize2
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

interface ImagePreviewModalProps {
  images: string[];
  initialIndex?: number;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
}

export function ImagePreviewModal({
  images,
  initialIndex = 0,
  isOpen,
  onClose,
  title = 'Review Images',
  description
}: ImagePreviewModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setIsLoading(true);
      setHasError(false);
      setZoom(1);
      setRotation(0);
    }
  }, [isOpen, initialIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, images.length]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    resetImageState();
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    resetImageState();
  };

  const resetImageState = () => {
    setIsLoading(true);
    setHasError(false);
    setZoom(1);
    setRotation(0);
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.5, 0.5));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleReset = () => {
    setZoom(1);
    setRotation(0);
  };

  const handleOpenInNewTab = () => {
    window.open(images[currentIndex], '_blank');
  };

  if (!images || images.length === 0) {
    return null;
  }

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[10003] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ 
              type: "spring",
              duration: 0.5,
              bounce: 0.3 
            }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] flex flex-col overflow-hidden"
          >
            {/* Enhanced Header */}
            <div className="relative bg-gradient-to-r from-purple-600 to-indigo-600 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <ImageIcon className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-2xl font-bold text-white"
                    >
                      {title}
                    </motion.h2>
                    <motion.div 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="flex items-center gap-4 text-white/80 text-sm mt-1"
                    >
                      {description && <span className="font-medium">{description}</span>}
                      <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                        {currentIndex + 1} of {images.length}
                      </Badge>
                    </motion.div>
                  </div>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={onClose}
                    className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Image Display */}
            <div className="relative flex-1 bg-gray-50 flex items-center justify-center min-h-[500px] overflow-hidden">
              {/* Navigation Buttons */}
              {images.length > 1 && (
                <>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10"
                  >
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handlePrevious}
                      className="bg-white/90 hover:bg-white text-gray-700 shadow-lg border border-gray-200 rounded-full h-12 w-12 p-0"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10"
                  >
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleNext}
                      className="bg-white/90 hover:bg-white text-gray-700 shadow-lg border border-gray-200 rounded-full h-12 w-12 p-0"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  </motion.div>
                </>
              )}

              {/* Image Controls */}
              <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleZoomIn}
                    className="bg-white/90 hover:bg-white text-gray-700 shadow-lg border border-gray-200 rounded-lg h-9 w-9 p-0"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleZoomOut}
                    className="bg-white/90 hover:bg-white text-gray-700 shadow-lg border border-gray-200 rounded-lg h-9 w-9 p-0"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRotate}
                    className="bg-white/90 hover:bg-white text-gray-700 shadow-lg border border-gray-200 rounded-lg h-9 w-9 p-0"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReset}
                    className="bg-white/90 hover:bg-white text-gray-700 shadow-lg border border-gray-200 rounded-lg h-9 px-3"
                  >
                    Reset
                  </Button>
                </motion.div>
              </div>

              {/* Main Image */}
              <div className="w-full h-full flex items-center justify-center p-8">
                {isLoading && (
                  <div className="flex items-center justify-center">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="h-12 w-12 border-4 border-purple-600 border-t-transparent rounded-full"
                    />
                  </div>
                )}
                {hasError && (
                  <div className="text-center text-gray-500">
                    <ImageIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm font-medium">Failed to load image</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={resetImageState}
                      className="mt-2"
                    >
                      Retry
                    </Button>
                  </div>
                )}
                <motion.img
                  src={images[currentIndex]}
                  alt={`Review image ${currentIndex + 1}`}
                  className={`max-w-full max-h-full object-contain rounded-lg shadow-xl cursor-move transition-all duration-300 ${
                    isLoading || hasError ? 'opacity-0' : 'opacity-100'
                  }`}
                  style={{
                    transform: `scale(${zoom}) rotate(${rotation}deg)`,
                    transformOrigin: 'center'
                  }}
                  onLoad={() => setIsLoading(false)}
                  onError={() => {
                    setIsLoading(false);
                    setHasError(true);
                  }}
                  animate={{
                    scale: zoom,
                    rotate: rotation
                  }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              </div>
            </div>

            {/* Enhanced Footer */}
            <div className="p-6 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex items-center justify-between">
                {/* Thumbnail Navigation */}
                {images.length > 1 && (
                  <div className="flex items-center gap-2 overflow-x-auto max-w-[60%] pb-1">
                    {images.map((image, index) => (
                      <motion.button
                        key={index}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setCurrentIndex(index);
                          resetImageState();
                        }}
                        className={`flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-all shadow-lg ${
                          index === currentIndex
                            ? 'border-purple-600 shadow-purple-600/25'
                            : 'border-gray-200 hover:border-purple-400'
                        }`}
                      >
                        <img
                          src={image}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </motion.button>
                    ))}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center gap-3">
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleOpenInNewTab}
                      className="bg-white hover:bg-gray-50 border-gray-200 text-gray-700 shadow-lg rounded-xl"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open in New Tab
                    </Button>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  // Use portal to render modal at document root level
  return typeof document !== 'undefined' 
    ? ReactDOM.createPortal(modalContent, document.body)
    : null;
}